import { gql } from '@apollo/client';

import { ClinicianChipFragment } from '../../components/clinicians/ClinicianChip';
import { ClinicianOptionFragment } from '../../components/clinicians/OptionalClinicianSelector';
import { VisitStateChangeItemFragment } from '../../components/comms/CommsVisitStateChangeItem';
import { DisciplineChipFragment } from '../../components/disciplines/DisciplineChip';
import { DisciplineTagFragment } from '../../components/disciplines/DisciplineTag';
import { DocumentListItemFragment } from '../../components/displayFields/documents';
import { QualificationOptionFragment } from '../../components/formFields/QualificationsField';
import { OrderChipFragment } from '../../components/orders/OrderChip';
import { OrderStatusFragment } from '../../components/orders/OrderStatus';
import { PatientChipFragment } from '../../components/patients/PatientChip';
import { SyncStatusFragment } from '../../components/syncing/SyncStatusDisplay';
import { TerritoryOptionFragment } from '../../components/territories/TerritoryAutoCompleteMoreOptions';
import { VisitOrdersListItemFragment } from '../../components/visitOrders/VisitOrdersTable';
import { VisitRequestActionsCellFragment } from '../../components/visitRequests/tables/VisitRequestActionsCell';
import { VisitActionsCellFragment } from '../../components/visits/tables/VisitActionsCell';
import { ClinicianVisitPerformanceFragment } from '../../pages/ClinicianDetailsPage/ClinicianPerformanceStats';
import {
    ScheduleClinicianAppointmentFragment,
    ScheduleClinicianFragment,
    ScheduleClinicianReservationFragment,
    ScheduleShiftBlockFragment,
    ScheduleShiftFragment,
    ScheduleTentativeShiftBlockFragment,
    ScheduleUnscheduledVisitFragment,
    ScheduleVisitRequestFragment,
    TentativeClinicianAppointmentFragment,
} from '../../pages/SchedulePage/ClinicianSchedulesTab/ClinicianSchedulesCalendarEvents';
import { VisitAvailabilityFragment } from '../../pages/VisitBooking/scheduling/BookConfirmed';
import { ExternalIdFragment } from '../../types/externalId';
import { BasicAddressFragment, BasicAddressWithCoordinatesFragment } from '../../utils/addresses';
import { AddressWithLatLngFragment } from '../sharedSchemas/addresses';

/**
 * Common fragments that are registered globally in Apollo Client.
 * These fragments can be used in queries without explicitly importing them.
 *
 * Note: Fragments are still defined co-located with their components/types,
 * but are collected here for registration in the InMemoryCache.
 *
 * Only small, frequently-reused fragments (≤10 fields) are registered here.
 * Large or rarely-used fragments should be imported locally where needed.
 */
export const commonFragments = gql`
    # Address fragments
    ${BasicAddressFragment}
    ${BasicAddressWithCoordinatesFragment}
    ${AddressWithLatLngFragment}

    # Clinician fragments
    ${ClinicianChipFragment}
    ${ClinicianOptionFragment}
    ${ClinicianVisitPerformanceFragment}

    # Communication fragments
    ${VisitStateChangeItemFragment}

    # Discipline fragments
    ${DisciplineChipFragment}
    ${DisciplineTagFragment}

    # Document fragments
    ${DocumentListItemFragment}

    # External ID fragments
    ${ExternalIdFragment}

    # Order fragments
    ${OrderChipFragment}
    ${OrderStatusFragment}

    # Patient fragments
    ${PatientChipFragment}

    # Qualification fragments
    ${QualificationOptionFragment}

    # Schedule fragments
    ${ScheduleShiftFragment}
    ${ScheduleShiftBlockFragment}
    ${ScheduleTentativeShiftBlockFragment}
    ${ScheduleClinicianAppointmentFragment}
    ${ScheduleClinicianReservationFragment}
    ${TentativeClinicianAppointmentFragment}
    ${ScheduleClinicianFragment}
    ${ScheduleUnscheduledVisitFragment}
    ${ScheduleVisitRequestFragment}

    # Syncing fragments
    ${SyncStatusFragment}

    # Territory fragments
    ${TerritoryOptionFragment}

    # Visit fragments
    ${VisitActionsCellFragment}
    ${VisitAvailabilityFragment}
    ${VisitOrdersListItemFragment}

    # Visit request fragments
    ${VisitRequestActionsCellFragment}
`;
